import { useQuery, useQueryClient } from '@tanstack/react-query';
import { orderBy } from 'natural-orderby';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AccentLabel,
    AnimatedPageLoader,
    HorizontalFlex,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    PadContainer,
    SingleSelect,
    Ticket,
    TicketBody,
    TicketHeader,
    VerticalFlex,
} from 'smartsuite-ui';
import { getEnrollmentUri } from '../constants/apiConstants.js';
import { airlines, validAirlines } from '../constants/flightEntryConstants.js';
import { isFlightNumberValid, isPnrValid } from '../utils/validationUtil.js';

function ModifyFlightDetailsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { reservationId, reservationLastName } = useParams();
    const [passengers, setPassengers] = useState([]);
    const transformedPassengers = passengers.map((passenger) => ({
        ...passenger,
        airlineCode: passenger?.airline?.airlineCode ?? '',
    }));
    const [isIneligibleModalOpen, setIneligibleModalOpen] = useState(false);

    const updatedPassengers = queryClient.getQueryData([
        'enrollment',
        reservationId,
        reservationLastName,
        'stateData',
    ])?.passengers;

    const enrollmentQuery = useQuery({
        queryKey: ['enrollment', reservationId, reservationLastName],
        queryFn: async () => {
            const response = await fetch(
                getEnrollmentUri(reservationId, reservationLastName),
            );

            if (!response.ok) {
                navigate('/');
            }
            const parsedResponse = await response.json();

            if (updatedPassengers) {
                return { ...parsedResponse, passengers: updatedPassengers };
            }

            const orderedPassengers = orderBy(
                parsedResponse.passengers,
                ['isPrimary'],
                ['desc'],
            );

            return {
                ...parsedResponse,
                passengers: orderedPassengers.map((passenger) => ({
                    ...passenger,
                    airline: airlines.find(
                        (airline) =>
                            airline.airlineCode === passenger.airlineCode,
                    ),
                })),
            };
        },
        onSuccess: (data) => {
            setPassengers(data?.passengers);
        },
    });

    const isPassengerEligible = (passenger) => {
        return (
            isPnrValid(passenger?.pnr) &&
            validAirlines.includes(passenger.airline?.airlineCode)
        );
    };

    const handlePassengerValueChange = (paxId, field, value) => {
        setPassengers((previousState) => {
            const index = previousState.findIndex(
                (passenger) => passenger.paxId === paxId,
            );

            previousState[index] = {
                ...previousState[index],
                [field]: value,
                isEligible: isPassengerEligible({
                    ...previousState[index],
                    [field]: value,
                }),
            };
            return [...previousState];
        });
    };

    const onAirlineChange = (selectedOption, paxId) => {
        const passenger = passengers.find(
            (passenger) => passenger.paxId === paxId,
        );
        if (selectedOption?.airlineCode === passenger.airline?.airlineCode) {
            return;
        }
        handlePassengerValueChange(paxId, 'airline', selectedOption);
    };

    const handleOptOutChange = (paxId, optedOutTimestamp) => {
        handlePassengerValueChange(paxId, 'optedOutTimestamp', optedOutTimestamp);
        handlePassengerValueChange(paxId, 'pnr', '');
        handlePassengerValueChange(paxId, 'airline', null);
        handlePassengerValueChange(paxId, 'flightNumber', '');
    };

    const applyFlightInfoToAll = () => {
        const primaryPassenger = passengers[0];
        if (!primaryPassenger) {
            return;
        }

        const updatedPassengers = passengers.map((passenger, index) => {
            if (index === 0) {
                return passenger;
            }
            return {
                ...passenger,
                airline: primaryPassenger.airline,
                flightNumber: primaryPassenger.flightNumber,
                pnr: primaryPassenger.pnr,
                isEligible: primaryPassenger.isEligible,
                optedOutTimestamp: primaryPassenger.optedOutTimestamp,
            };
        });
        setPassengers(updatedPassengers);
    };

    const numberOfEligiblePassengers = passengers.filter(
        (passenger) => passenger?.isEligible,
    ).length;

    const isEveryPassengerValid = passengers.every(
        (passenger) =>
            (!passenger?.pnr && !passenger?.airline) ||
            passenger?.isEligible ||
            passenger?.airline?.airlineName === 'Other',
    );

    const isNextButtonEnabled =
        numberOfEligiblePassengers > 0 && isEveryPassengerValid;

    const handleCancel = () => {
        queryClient.resetQueries({
            queryKey: [
                'enrollment',
                reservationId,
                reservationLastName,
                'stateData',
            ],
            exact: true,
        });

        navigate('..');
    };

    const handleSubmit = () => {
        queryClient.setQueryData(
            ['enrollment', reservationId, reservationLastName, 'stateData'],
            { ...enrollmentQuery.data, passengers: transformedPassengers },
        );

        navigate(`../modifyContactDetails`);
    };

    if (!passengers.length) {
        return <AnimatedPageLoader />;
    }

    const renderPassengerTickets = () => {
        if (!passengers.length) {
            return null;
        }

        return passengers.map((passenger, index) => {
            const showEligibleBadge =
                passenger?.isEligible ||
                passenger?.airline?.airlineName === 'Other';
            const isEligibleString = passenger?.isEligible
                ? 'eligible'
                : 'ineligible';

            const applyAllButton =
                index === 0 && passengers.length > 1 ? (
                    <>
                        <PadContainer cssClassModifiers={['pad-small']} />
                        <HorizontalFlex
                            cssClassModifiers={[
                                'justify-center',
                                'align-center',
                            ]}
                        >
                            <button
                                className="btn btn--primary"
                                onClick={applyFlightInfoToAll}
                                disabled={!passenger?.isEligible}
                            >
                                {t('applyFlightInfoToAllTravelers')}
                            </button>
                        </HorizontalFlex>
                    </>
                ) : null;

            return (
                <div key={passenger.paxId}>
                    <Ticket>
                        <TicketHeader>
                            <VerticalFlex>
                                <AccentLabel>Guest Name</AccentLabel>
                                <Label>{`${passenger.lastName}, ${passenger.firstName}`}</Label>
                            </VerticalFlex>
                            <PadContainer cssClassModifiers={['pad-small']} />
                            {passenger.isPrimary && (
                                <>
                                    <span className="modify-flight-details__badge modify-flight-details__badge--primary-guest">
                                        Primary Guest
                                    </span>
                                    <PadContainer
                                        cssClassModifiers={['pad-small']}
                                    />
                                </>
                            )}
                            {showEligibleBadge && (
                                <span
                                    className={`reservation-flight-details__badge reservation-flight-details__badge--${isEligibleString}`}
                                >
                                    {t(isEligibleString)}
                                </span>
                            )}
                        </TicketHeader>
                        <TicketBody>
                            <PadContainer cssClassModifiers={['pad-small']}>
                                <SingleSelect
                                    cssClassModifiers={['small-relative']}
                                    label={t('airlineLabel')}
                                    options={airlines}
                                    isClearable={true}
                                    value={
                                        airlines.length === 1
                                            ? airlines[0]
                                            : passenger.airline || null
                                    }
                                    onChange={(event) =>
                                        onAirlineChange(
                                            event,
                                            passenger.paxId,
                                        )
                                    }
                                    placeholder={t('selectAirline')}
                                    textField="airlineName"
                                    valueField="airlineCode"
                                    errorMessage="Please fill out an airline."
                                    isValid={
                                        !isPnrValid(passenger?.pnr) ||
                                        !!passenger.airline
                                    }
                                    disabled={
                                        !!passenger?.optedOutTimestamp
                                    }
                                />
                                <div className="modify-flight-details__shared-fields-container">
                                    <Input
                                        cssClassModifiers={['medium-relative']}
                                        label={t('flightNumberLabel')}
                                        placeholder={t(
                                            'flightNumberPlaceholder',
                                        )}
                                        value={passenger.flightNumber}
                                        maxLength={4}
                                        onChange={(event) =>
                                            handlePassengerValueChange(
                                                passenger.paxId,
                                                'flightNumber',
                                                event.target.value,
                                            )
                                        }
                                        errorMessage={
                                            'Enter a 1-4 digit flight number.'
                                        }
                                        isValid={
                                            !passenger.flightNumber ||
                                            isFlightNumberValid(
                                                passenger.flightNumber,
                                            )
                                        }
                                        disabled={
                                            !!passenger?.optedOutTimestamp
                                        }
                                    />
                                    <Input
                                        label={t('pnrLabel')}
                                        placeholder={t('pnrPlaceholder')}
                                        value={passenger.pnr}
                                        maxLength={6}
                                        onChange={(event) =>
                                            handlePassengerValueChange(
                                                passenger.paxId,
                                                'pnr',
                                                event.target.value.toUpperCase(),
                                            )
                                        }
                                        errorMessage={
                                            'Please enter a 6 character alphanumeric PNR.'
                                        }
                                        isValid={
                                            (!passenger.pnr &&
                                                !passenger.airline) ||
                                            passenger?.airline?.airlineName ===
                                            'Other' ||
                                            isPnrValid(passenger.pnr)
                                        }
                                        disabled={!!passenger?.optedOutTimestamp}
                                    />
                                </div>
                                <label className="reservation-flight-details__checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={!!passenger?.optedOutTimestamp}
                                        onChange={() =>
                                            handleOptOutChange(
                                                passenger.paxId,
                                                passenger?.optedOutTimestamp
                                                    ? null
                                                    : new Date().toISOString(),
                                            )
                                        }
                                    />
                                    <span>
                                        Opt Out (Lap Child, Infant, or Not
                                        Flying)
                                    </span>
                                </label>
                            </PadContainer>
                        </TicketBody>
                    </Ticket>
                    {applyAllButton}
                </div>
            );
        });
    };

    return (
        <div className="page-wrapper">
            <div className="modify-flight-details">
                <div>
                    <h1 className="modify-flight-details__title">
                        {/* {t('modifyflightInformationTitle')} */}
                        Modify Flight Information
                    </h1>
                    <p className="instruction-text">
                        {/* {t('modifyFlightInformationInstructions')} */}
                        Modify your flight details up to the day of travel. You
                        can apply the same flight information to all guests if
                        they're on the same flight confirmation number.
                    </p>
                </div>
                <div className="modify-flight-details__tickets-container">
                    {renderPassengerTickets()}
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                {/* <div className="modify-flight-details__navigate-btns-container"> */}
                <HorizontalFlex cssClassModifiers={['justify-center']}>
                    <button
                        className="btn btn--secondary"
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <PadContainer cssClassModifiers={['pad-small']} />
                    <button
                        className="btn btn--primary"
                        disabled={!isNextButtonEnabled}
                        onClick={() => {
                            numberOfEligiblePassengers < passengers.length
                                ? setIneligibleModalOpen(true)
                                : handleSubmit();
                        }}
                    >
                        {`${t('next')} - ${numberOfEligiblePassengers} / ${passengers.length
                            } ${t('eligible')}`}
                    </button>
                </HorizontalFlex>
                {isIneligibleModalOpen && (
                    <Modal onOverlayClick={() => setIneligibleModalOpen(false)}>
                        <ModalHeader
                            onDismiss={() => setIneligibleModalOpen(false)}
                        >
                            Confirm Flight Info Modifications?
                        </ModalHeader>
                        <ModalBody>
                            The following Guests are ineligible:
                            <ul>
                                {passengers
                                    .filter(
                                        (passenger) => !passenger?.isEligible,
                                    )
                                    .map((passenger, index) => (
                                        <div key={passenger.paxId}>
                                            {index > 0 && (
                                                <PadContainer
                                                    cssClassModifiers={[
                                                        'pad-small',
                                                    ]}
                                                />
                                            )}
                                            <li>
                                                {`${passenger.lastName}, ${passenger.firstName}`}
                                            </li>
                                        </div>
                                    ))}
                            </ul>
                        </ModalBody>
                        <ModalFooter>
                            <HorizontalFlex
                                cssClassModifiers={['justify-flex-end']}
                            >
                                <button
                                    className="btn btn--primary"
                                    onClick={handleSubmit}
                                >
                                    {t('confirm')}
                                </button>
                                <button
                                    className="btn btn--secondary"
                                    onClick={() =>
                                        setIneligibleModalOpen(false)
                                    }
                                >
                                    {t('back')}
                                </button>
                            </HorizontalFlex>
                        </ModalFooter>
                    </Modal>
                )}
            </div>
        </div>
    );
}

export default ModifyFlightDetailsPage;
